import {
  useState,
  useRef,
  useEffect,
  FC,
  useCallback,
  SyntheticEvent,
} from 'react'
import styles from './styles.module.scss'
import { useDisableScroll, useStyleOverride } from '@mettle/hooks'
import downArrow from '@/assets/icons/dropdown.svg'
import downArrowWhite from '@/assets/icons/dropdown_white.svg'
import cx from 'classnames'
import { Link } from '@/components/Link'
import { handleNavigation, urlIsExternal } from '@/components/Link/hooks'
import {
  MenuLink,
  MenuSubLink,
  useMenuLinks,
} from '@/components/Navigation/hooks/useMenuLinks'
import { useExternalNavigation } from '@/hooks/useAnalytics'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { usePathname, useRouter } from 'next/navigation'
import { DynamicImage } from '@/components/DynamicImage'

export const AnimatedMenuLinks: FC<{
  className?: string
  openClassName?: string
  isMenuClosing: boolean
  hideMenu(): void
}> = ({ className, openClassName, isMenuClosing, hideMenu }) => {
  useDisableScroll()
  useStyleOverride(document.body, {
    // @ts-expect-error CSS property missing from type
    'overscroll-behavior': 'none',
  })

  return (
    <ul className={cx(className, styles.links)}>
      {useMenuLinks().map(
        (
          { id, title, subtitle, children, nestedSections, isDefault, url },
          index,
          // oxlint-disable-next-line no-unused-vars
          links,
        ) => {
          if (nestedSections) {
            nestedSections.unshift({
              id,
              title,
              headerLinks: children || undefined,
              subtitle: subtitle,
              children: children,
            })
          }

          return (
            <MobileLink
              id={id}
              key={id}
              title={title}
              url={url}
              children={children} // oxlint-disable-line react/no-children-prop
              nestedSections={nestedSections}
              delayIn={100 + index * 50}
              isMenuClosing={isMenuClosing}
              hideMenu={hideMenu}
              openClassName={openClassName}
              isDefault={isDefault}
              primary
            />
          )
        },
      )}
    </ul>
  )
}

export interface MobileLinkProps extends MenuLink {
  delayIn: number
  isMenuClosing: boolean
  openClassName?: string
  secondary?: boolean
  primary?: boolean
  isDefault?: boolean
  url?: string

  hideMenu(): void
}

export const MobileLink: FC<MobileLinkProps> = ({
  id,
  title,
  children,
  delayIn,
  isMenuClosing,
  openClassName,
  hideMenu,
  nestedSections = [],
  secondary,
  primary,
  isDefault,
  url,
}) => {
  const logExternalNavigation = useExternalNavigation()
  const router = useRouter()
  const nodeRef = useRef(null)
  const subLinksRef = useRef(null)

  const navigate = useCallback(
    (url: string) => {
      hideMenu()
      logExternalNavigation(url, urlIsExternal(url))
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      handleNavigation(router, url)({} as SyntheticEvent<any>)
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [hideMenu],
  )

  const [isExpanded, setIsExpanded] = useState(false)

  const dynamicProps = () => {
    if (url && nestedSections.length === 0) {
      return {
        url,
      }
    } else {
      return {
        onClick: () => setIsExpanded(!isExpanded),
      }
    }
  }

  return (
    <li
      key={id}
      className={cx({ [styles.initiallyHidden]: !isDefault && !secondary })}
    >
      <TransitionGroup component={null}>
        {!isMenuClosing && (
          <CSSTransition
            nodeRef={nodeRef}
            timeout={300}
            classNames={{
              enter: styles.fadeEnter,
              enterActive: styles.fadeEnterActive,
              exit: styles.fadeExit,
              exitActive: styles.fadeExitActive,
            }}
            appear
          >
            <div ref={nodeRef} style={{ transitionDelay: `${delayIn}ms` }}>
              <Link
                className={cx({
                  [styles.open]: isExpanded,
                  [styles.secondary]: secondary,
                  [openClassName!]: openClassName && isExpanded,
                })}
                {...dynamicProps()}
              >
                <span className={cx({ [styles.initialTitle]: primary })}>
                  {title}
                </span>
                {children && <DropdownIcon white expanded={isExpanded} />}
              </Link>
              {children && (
                <TransitionGroup component={null}>
                  {isExpanded && (
                    <CSSTransition
                      nodeRef={subLinksRef}
                      timeout={250}
                      classNames={{
                        enter: styles.slideEnter,
                        enterActive: styles.slideEnterActive,
                        exit: styles.slideExit,
                        exitActive: styles.slideExitActive,
                      }}
                    >
                      <div ref={subLinksRef} data-testid="sub-links">
                        {nestedSections.length === 0 ? (
                          <MobileSubLinks
                            links={children}
                            navigate={navigate}
                          />
                        ) : (
                          <ul className={styles.nestedSection}>
                            {nestedSections.map(
                              ({ id, subtitle, children }, index) => (
                                <MobileLink
                                  id={id}
                                  key={id}
                                  title={subtitle!}
                                  children={children!} // oxlint-disable-line react/no-children-prop
                                  delayIn={100 + index * 50}
                                  isMenuClosing={isMenuClosing}
                                  hideMenu={hideMenu}
                                  openClassName={openClassName}
                                  secondary
                                />
                              ),
                            )}
                          </ul>
                        )}
                      </div>
                    </CSSTransition>
                  )}
                </TransitionGroup>
              )}
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </li>
  )
}

export const MobileSubLinks: React.FC<{
  links: MenuSubLink[]
  navigate(url: string): void
}> = ({ links, navigate }) => (
  <ul data-testid="mobile-sub-links" className={styles.mobileSubLinks}>
    {links.map(({ id, title, url, isNew }) => (
      <li key={id} data-testid={url}>
        <Link url={url} onClick={() => navigate(url)}>
          {title}
          {isNew && <NewPill />}
        </Link>
      </li>
    ))}
  </ul>
)

const NewPill = () => <span className={styles.newPill}>New</span>

export const MenuLinks: React.FC<{
  className?: string
}> = ({ className }) => {
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null)
  const pathname = usePathname()

  useEffect(() => {
    setHoveredMenuItem(null)
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [pathname])

  return (
    <>
      <ul className={cx(styles.links, className)}>
        {useMenuLinks().map(
          ({
            id,
            title,
            subtitle,
            children,
            url,
            nestedSections,
            isDefault,
          }) => (
            <li
              data-id={id}
              key={id}
              className={cx(styles.initial, {
                [styles.hovered]: hoveredMenuItem === id,
                [styles.initiallyHidden]: !isDefault,
              })}
              onMouseEnter={e => {
                setHoveredMenuItem(id)

                if (e.currentTarget.parentNode) {
                  const siblings = Array.from(
                    e.currentTarget.parentNode.children,
                  ).filter(item => item !== e.currentTarget)

                  siblings.forEach((item: Element) => {
                    const child = item.firstElementChild as HTMLAnchorElement

                    child.blur()
                  })
                }
              }}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <>
                <Link
                  allowPartialActive
                  url={url}
                  activeClassName={styles.activeLink}
                  onClick={children ? () => {} : undefined}
                >
                  {title}
                  {children && <DropdownIcon white />}
                </Link>
                {children && (
                  <div
                    className={cx(styles.sublinks, {
                      [styles.fullWidth]: (nestedSections?.length || 0) > 1,
                      [styles.col2]: nestedSections?.length === 1,
                      [styles.col3]: nestedSections?.length === 2,
                      [styles.col4]: nestedSections?.length === 3,
                    })}
                    data-testid="sub-links"
                  >
                    <ul>
                      {nestedSections?.length && (
                        <li className={styles.subtitle}>{subtitle}</li>
                      )}
                      {children.map(
                        ({ id, url, title, isNew }: MenuSubLink) => (
                          <li key={id} data-testid={url}>
                            <Link
                              allowPartialActive={url?.split('/').length > 3}
                              activeClassName={styles.activeLink}
                              url={url}
                            >
                              {title}
                              {isNew && <NewPill />}
                            </Link>
                          </li>
                        ),
                      )}
                    </ul>

                    {nestedSections?.map(section => {
                      return (
                        <ul key={section.id}>
                          <li className={styles.subtitle}>
                            {section.subtitle}
                          </li>
                          {section.children?.map(
                            ({ id, url, title, isNew }: MenuSubLink) => (
                              <li key={id} data-testid={url}>
                                <Link
                                  allowPartialActive={
                                    url?.split('/').length > 3
                                  }
                                  activeClassName={styles.activeLink}
                                  url={url}
                                >
                                  {title}
                                  {isNew && <NewPill />}
                                </Link>
                              </li>
                            ),
                          )}
                        </ul>
                      )
                    })}
                  </div>
                )}
              </>
            </li>
          ),
        )}
      </ul>
    </>
  )
}

export const DropdownIcon: React.FC<{
  expanded?: boolean
  white?: boolean
}> = ({ expanded, white }) => (
  <div
    data-testid="dropdown-icon"
    className={cx(styles.dropdownIcon, {
      [styles.open]: expanded,
    })}
  >
    <DynamicImage
      alt="Expand menu"
      image={white ? downArrowWhite : downArrow}
      sizes="24px"
      local
    />
  </div>
)
